import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "剧情介绍", to: "/plot" },
  { label: "影评", to: "/reviews" },
  { label: "资讯", to: "/news" },
  { label: "影片资料", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || mobileOpen
          ? "border-b border-background-800 bg-background-950/95 backdrop-blur-md"
          : "border-b border-transparent bg-gradient-to-b from-background-950/80 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-background-50">
            <i className="ri-leaf-line text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold tracking-wide text-foreground-50">
              激情丛林
            </span>
            <span className="mt-0.5 font-label text-[10px] uppercase tracking-[0.25em] text-accent-500">
              Passion Jungle
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `rounded-md px-3.5 py-2 font-label text-sm transition-colors ${
                  isActive
                    ? "bg-primary-500/15 text-accent-400"
                    : "text-foreground-300 hover:bg-background-800/60 hover:text-foreground-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden w-64 items-center gap-2 rounded-md border border-background-700 bg-background-900/70 px-3 py-2 transition-colors focus-within:border-primary-500 md:flex"
        >
          <i className="ri-search-line text-base text-foreground-400"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索剧情 / 影评 / 资讯"
            className="w-full bg-transparent font-label text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none"
            aria-label="站内搜索"
          />
        </form>

        <Link
          to="/search"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-200 transition-colors hover:border-primary-500 hover:text-accent-400 md:hidden"
          aria-label="搜索"
        >
          <i className="ri-search-line text-lg"></i>
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-200 transition-colors hover:border-primary-500 lg:hidden"
          aria-label="展开菜单"
          aria-expanded={mobileOpen}
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-800 bg-background-950/95 px-4 pb-6 pt-4 lg:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-4 flex items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-2.5"
          >
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索剧情 / 影评 / 资讯"
              className="w-full bg-transparent font-label text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none"
              aria-label="站内搜索"
            />
          </form>
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 font-label text-sm transition-colors ${
                    isActive
                      ? "bg-primary-500/15 text-accent-400"
                      : "text-foreground-200 hover:bg-background-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}